import {Injectable, OnDestroy} from '@angular/core';
import {AppRightPaneService, BackdropContainerEnum} from '@mydocma/app-container';
import {BackdropService} from '@mydocma/component-library/src/lib/backdrop';
import {PromptDialogService} from '@mydocma/component-library/src/lib/prompt-dialog';
import {TranslateService} from '@ngx-translate/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject} from 'rxjs';
import {MmFaultDetailFormComponent} from '../../mm-fault/mm-fault-detail-form/mm-fault-detail-form.component';
import {MmFaultRightPanePageEnum} from './mm-fault-right-pane.enum';
import {MmPlanEditService} from '../../mm-plans-view/mm-plan-view/services/mm-plan-edit.service';
import {FaultsBackdropContainerEnum} from '../../faults-backdrop-container.enum';
import {FaultData} from '@docma-mm-core/fault/fault-data';
import {MmFaultRightPaneService} from './mm-fault-right-pane.service';
import { PlanEditModeEnum } from '../../mm-plans-view/mm-plan-view/services/plan-edit-mode.enum';
import { MmTranslateService } from '../../../mm-translate.service';


@UntilDestroy()
@Injectable()
export class MmFaultChangePageService implements OnDestroy {

  changesPerMask: Map<MmFaultRightPanePageEnum, BehaviorSubject<boolean>>;
  private faultDetail: MmFaultDetailFormComponent;


  constructor(
     public readonly appRightPaneService: AppRightPaneService,
     public readonly planEditService: MmPlanEditService,
     private readonly backdropService: BackdropService,
     private readonly translateService: MmTranslateService,
     private readonly dialogService: PromptDialogService,
     private readonly faultRightPaneService: MmFaultRightPaneService) {

    this.planEditService.attachTo(this);
    this.changesPerMask = new Map<MmFaultRightPanePageEnum, BehaviorSubject<boolean>>();

    this.changesPerMask.set(MmFaultRightPanePageEnum.fault, new BehaviorSubject<boolean>(false));
    this.changesPerMask.set(MmFaultRightPanePageEnum.editFault, new BehaviorSubject<boolean>(false));
    this.changesPerMask.set(MmFaultRightPanePageEnum.addActivity, new BehaviorSubject<boolean>(false));
    this.changesPerMask.set(MmFaultRightPanePageEnum.editActivity, new BehaviorSubject<boolean>(false));
    this.changesPerMask.set(MmFaultRightPanePageEnum.addAttachments, new BehaviorSubject<boolean>(false));
    this.changesPerMask.set(MmFaultRightPanePageEnum.plan, new BehaviorSubject<boolean>(false));
    this.changesPerMask.set(MmFaultRightPanePageEnum.fieldConfiguration, new BehaviorSubject<boolean>(false));


    this.backdropService.onBackdropClick
      .pipe(untilDestroyed(this))
      .subscribe( s => this.showBackdropDialog());
  }

  hasChanges(mask: MmFaultRightPanePageEnum) {
    return this.changesPerMask.get(mask)?.value;
  }

  ngOnDestroy(): void {
  }

  async setChanges(mask: MmFaultRightPanePageEnum) {

    const sub = this.changesPerMask.get(mask);
    if (sub) {
      sub.next(true);
    } else {
      this.changesPerMask.set(mask, new BehaviorSubject<boolean>(true));
    }

    this.appRightPaneService.hideBackdrop();
    if (this.planEditService.planEditMode !== PlanEditModeEnum.None) {
      this.appRightPaneService.showBackdrop(false,
        [ BackdropContainerEnum.MydocmaMainPane,
          FaultsBackdropContainerEnum.FaultsPlanView,
          FaultsBackdropContainerEnum.FaultsPlanFlyOut]);
    } else {
      this.appRightPaneService.showBackdrop(false,
        [ FaultsBackdropContainerEnum.FaultsChips,
          FaultsBackdropContainerEnum.FaultsGrid,
          FaultsBackdropContainerEnum.FaultsHeader,
          FaultsBackdropContainerEnum.FaultsPlanFlyOut,
          FaultsBackdropContainerEnum.FaultsPlanHeader,
          FaultsBackdropContainerEnum.FaultsPlanView]);
    }
  }

  async disableChanges(mask: MmFaultRightPanePageEnum) {
    const sub = this.changesPerMask.get(mask);
    if (sub) {
      sub.next(false);
    } else {
      this.changesPerMask.set(mask, new BehaviorSubject<boolean>(false));
    }

    let hasChanges = false;
    if (mask === MmFaultRightPanePageEnum.editFault) {
      if (sub.value){
        hasChanges = true;
      }
    }
    else {
      for (const changeOnMask of this.changesPerMask.values()) {
        if (changeOnMask.value) {
          hasChanges = true;
        }
      }
    }

    if (!hasChanges) {
      this.appRightPaneService.hideBackdrop();
    }
  }

  private async showBackdropDialog() {
    const title = await this.translateService.get('MM.dialog.changeDialog.title').toPromise();
    const content = await this.translateService.get('MM.dialog.changeDialog.content').toPromise();

    return (await this.dialogService.showCompleteChangesNotificationDialog({title, content}))?.value;
  }


  setFaultDetail(detailComponent: MmFaultDetailFormComponent) {
    this.faultDetail = detailComponent;
  }

  async getDetailChanges(): Promise<FaultData> {
    return this.faultDetail.getChanges();
  }


}
